import {useMemo} from 'react';

import {
    type CertificationBundle,
    useFetchBundleFromVoucherExam,
    useFetchCertificationBundlesForTopic,
} from '@udemy/react-certification-preparation';
import {useExamsByIds} from '@udemy/react-certification-vouchers';

import {pickBundleForVoucherExam} from 'src/lib/pick-bundle-for-voucher-exam';

export interface BundleCoursesForVoucher {
    bundle: CertificationBundle | undefined;
    courseIdsToUpsell: number[];
    isLoading: boolean;
}

export function useBundleCoursesForVoucher(
    offerId: string | undefined,
    cartCourseIds?: Set<number>,
): BundleCoursesForVoucher {
    const {exams, isLoading: examLoading} = useExamsByIds(offerId ? [offerId] : []);
    const topicId = exams[0]?.topic?.id;

    const {bundles: topicBundles, isLoading: topicBundlesLoading} =
        useFetchCertificationBundlesForTopic(topicId ?? '', {
            enabled: Boolean(topicId),
        });

    const needsDisambiguation = topicBundles.length > 1;
    const {bundle: voucherExamBundle, isLoading: voucherExamBundleLoading} =
        useFetchBundleFromVoucherExam(offerId ?? null, {
            enabled: Boolean(offerId) && needsDisambiguation,
        });

    const bundle = useMemo(
        () => pickBundleForVoucherExam(topicBundles, voucherExamBundle),
        [topicBundles, voucherExamBundle],
    );

    const courseIdsToUpsell = useMemo(
        () => bundle?.contents.map((c) => c.id).filter((id) => !cartCourseIds?.has(id)) ?? [],
        [bundle, cartCourseIds],
    );

    const isLoading =
        (Boolean(offerId) && examLoading) ||
        (Boolean(topicId) && topicBundlesLoading) ||
        (needsDisambiguation && voucherExamBundleLoading);

    return {bundle, courseIdsToUpsell, isLoading};
}
